import type { Module } from '@/types/course';

export const module2: Module = {
  slug: 'modulo-2-tipos-oro-inversion',
  number: 2,
  title: 'Tipos de oro de inversión',
  description:
    'No todo el oro es igual a efectos de inversión. Conoce los formatos disponibles, sus pros y contras, primas asociadas y casos de uso para distinguirte como inversor informado.',
  durationMinutes: 80,
  badge: 'conocedor-lingotes',
  lessons: [
    {
      slug: 'oro-fisico-vs-papel',
      number: '2.1',
      title: 'Oro físico vs oro papel',
      durationMinutes: 20,
      videoUrl: null,
      content: `La primera gran decisión: **¿quiero tener el metal en mi mano, o me basta con tener un derecho sobre él?** La respuesta determina absolutamente todo: rentabilidad neta, riesgo de contraparte, fiscalidad, custodia y capacidad de actuar en una crisis.

**Oro físico** significa tener lingotes y/o monedas en tu posesión directa o en custodia profesional segregada. Cuando compras un lingote certificado, ese lingote, con su número de serie, es jurídicamente tuyo. Si el distribuidor quiebra, si el banco cierra, si hay una crisis sistémica, el oro sigue siendo tuyo. **Cero riesgo de contraparte**.

**Oro papel** es cualquier instrumento financiero que representa oro sin que tú lo poseas físicamente:

- **ETFs de oro** (SPDR Gold Shares, iShares Gold Trust, Xetra-Gold): cotizan en bolsa, replican el precio spot, comisiones del 0,15-0,40% anual. Su debilidad: el oro está en bóvedas custodiado por bancos depositarios, y en una crisis sistémica grave podrías recibir el equivalente en efectivo, no oro físico. Algunos ETFs (Xetra-Gold) sí permiten conversión a oro físico, con costes adicionales.

- **Futuros y opciones de oro**: contratos derivados negociados en COMEX. Apalancamiento alto. Útiles para profesionales y especulación, pero **inadecuados para preservación patrimonial**.

- **Cuentas de oro asignado / no asignado**: ofrecidas por bancos privados. El asignado es físico y específico; el no asignado es una promesa contable.

- **Acciones de mineras de oro**: NO son oro. Son empresas que producen oro, con sus propios riesgos operativos, geopolíticos, de gestión y apalancamiento.

**¿Cuándo elegir físico?** Cuando el objetivo es **preservación patrimonial a largo plazo**, protección frente a crisis sistémicas, transmisión generacional, o cuando el monto invertido es significativo (>10.000€).

**¿Cuándo elegir papel?** Cuando el objetivo es exposición rápida y barata al precio del oro sin intención de tenerlo físicamente, para cantidades pequeñas, o como complemento táctico.

**La regla práctica**: para un inversor patrimonial español, el **oro físico de inversión certificado** es claramente superior. Las ventajas fiscales (exención de IVA), la ausencia de riesgo de contraparte y la posibilidad de transmisión hereditaria directa lo hacen óptimo.`,
      keyPoints: [
        'Oro físico = posesión directa, sin riesgo de contraparte. Oro papel = derecho sobre oro custodiado por terceros.',
        'ETFs son cómodos pero pueden no entregarte oro físico en una crisis sistémica.',
        'Futuros son herramientas profesionales, no instrumentos patrimoniales.',
        'Acciones de mineras NO son oro: responden a riesgos empresariales propios.',
        'Para preservación patrimonial a largo plazo, el oro físico certificado es la opción óptima.',
      ],
    },
    {
      slug: 'lingotes-certificacion-primas',
      number: '2.2',
      title: 'Lingotes: tamaños, certificación LBMA, primas',
      durationMinutes: 22,
      videoUrl: null,
      content: `El lingote es el formato clásico del oro de inversión. Pero "lingote" abarca desde piezas de 1 gramo hasta barras de 12,5 kg. Elegir el formato correcto puede ahorrarte cientos o miles de euros en primas.

**Tamaños estandarizados disponibles en el mercado profesional**:
- 1 g, 2,5 g, 5 g (pequeños, alta prima, ideales para regalo o ahorro inicial)
- 10 g, 20 g, 50 g (gama media, popular entre ahorradores)
- 100 g, 250 g, 500 g (gama profesional, primas reducidas)
- 1 kg (estándar institucional, prima mínima)
- 12,5 kg / 400 oz (Good Delivery Bar, formato mayorista)

**La certificación LBMA**. La London Bullion Market Association regula el mercado mayorista del oro y mantiene la **Good Delivery List**, una lista de refinerías cuyos lingotes son aceptados sin discusión en cualquier transacción profesional del mundo. Refinerías como **Argor-Heraeus** (Suiza), **PAMP Suisse**, **Valcambi**, **Umicore** (Bélgica), **Heraeus** (Alemania), **Rand Refinery** (Sudáfrica) o **Royal Canadian Mint** son referentes mundiales. Comprar lingotes de estas refinerías garantiza pureza, peso y aceptación universal.

**Pureza**. El oro de inversión debe tener una pureza mínima legal del **99,5%** (995/1000) para considerarse oro de inversión a efectos fiscales en la UE (exención de IVA). En la práctica, los lingotes modernos suelen ser del **99,99%** (cuatro nueves, "fino").

**Las primas según tamaño** (a mayor tamaño, menor prima porcentual):

- Lingote de 1 g: prima del 8-15% sobre spot
- Lingote de 10 g: prima del 4-7%
- Lingote de 100 g: prima del 2-3,5%
- Lingote de 1 kg: prima del 0,8-1,8%
- Lingote de 12,5 kg: prima del 0,3-0,8% (mercado institucional)

Esto crea una tensión: **cuanto más concentrada está la inversión, más barato es el oro, pero menos divisible**. Un lingote de 1 kg te da el mejor precio, pero si necesitas vender solo el 10%, debes vender el lingote entero. Una solución equilibrada: **diversificar tamaños**.

**Lingote acuñado vs colado**. Los lingotes pequeños suelen ser **acuñados** (minted): troquelados, acabado pulido, blister con certificado individual. Los grandes (>250 g) suelen ser **colados** (cast): el oro fundido se vierte en un molde. Los acuñados tienen prima ligeramente superior por su acabado estético; los colados son la opción más eficiente en coste por gramo.

**Certificación y serialización**. Cada lingote profesional viene con un **certificado de autenticidad**: número de serie único, peso exacto, pureza, refinería emisora y fecha. El blister es **inviolable**: manipularlo invalida el certificado. Por eso un lingote certificado debe **mantenerse en su blister original** desde la compra hasta la venta. Romper el blister reduce el precio de reventa.

**Cómo verificar autenticidad**:
1. Comprar siempre a distribuidor certificado (LBMA Good Delivery o asociado a entidad reconocida).
2. Verificar el número de serie en la web de la refinería.
3. Pesar el lingote: el oro tiene una densidad muy específica (19,32 g/cm³).
4. Para grandes cantidades, los distribuidores profesionales cuentan con verificadores ultrasónicos o de conductividad eléctrica.`,
      keyPoints: [
        'Los lingotes van de 1 g a 12,5 kg; a mayor tamaño, menor prima porcentual.',
        'La certificación LBMA garantiza pureza y aceptación mundial.',
        'Pureza mínima legal de inversión: 99,5%; lo habitual es 99,99%.',
        'Diversificar tamaños equilibra coste y flexibilidad de venta.',
        'Mantener siempre el blister original sellado: romperlo reduce el valor de reventa.',
      ],
    },
    {
      slug: 'monedas-bullion',
      number: '2.3',
      title: 'Monedas Bullion',
      durationMinutes: 22,
      videoUrl: null,
      content: `Las monedas bullion son el otro gran formato del oro de inversión. A diferencia de los lingotes, son acuñadas por casas de moneda nacionales, tienen valor facial nominal y, en muchos países, gozan de estatus de **moneda de curso legal**. Esto les da ventajas únicas en términos fiscales, de liquidez y de reconocimiento.

**Las cuatro grandes monedas bullion del mundo**:

**1. Krugerrand (Sudáfrica)** — Acuñada desde 1967 por la South African Mint. Es la moneda bullion más antigua y la más comercializada históricamente. Pureza: 91,67% (22 quilates), por lo que una Krugerrand de 1 onza pesa más de 1 onza (33,93 g) porque el resto es cobre que la hace más resistente al desgaste. Disponible en 1 oz, 1/2 oz, 1/4 oz y 1/10 oz. **Su gran ventaja**: liquidez universal y reconocimiento global.

**2. Maple Leaf (Canadá)** — Acuñada desde 1979 por la Royal Canadian Mint. Pureza: 99,99% (24 quilates "puros"). En 2007 se produjo una Maple Leaf especial de 99,999%. Diseño icónico de la hoja de arce, con elementos de seguridad avanzados (micro-grabado, hoja radial). Disponible en 1 oz, 1/2 oz, 1/4 oz, 1/10 oz, 1/20 oz. **Su ventaja**: pureza máxima y prestigio internacional.

**3. Britannia (Reino Unido)** — Acuñada por la Royal Mint británica desde 1987. Pureza original 91,67%, elevada a 99,99% desde 2013. Diseño con la figura de Britannia. Disponible en 1 oz, 1/2 oz, 1/4 oz, 1/10 oz. **Su ventaja única**: como moneda de curso legal británica, está **exenta de Capital Gains Tax para residentes en Reino Unido**. Para residentes españoles esa ventaja no aplica directamente, pero su prestigio y liquidez son altos.

**4. Filarmónica de Viena (Austria)** — Acuñada desde 1989 por la Münze Österreich. Pureza: 99,99%. La única moneda bullion con valor facial denominado en euros (100€ para la onza). Diseño con instrumentos de la Orquesta Filarmónica de Viena. Disponible en 1 oz, 1/2 oz, 1/4 oz, 1/10 oz, 1/25 oz. **Su ventaja**: la moneda bullion más popular en Europa continental; primas competitivas y excelente liquidez en España.

**Otras monedas relevantes**: American Eagle, American Buffalo (99,99%), Australian Kangaroo, China Panda, Mexican Libertad.

**Lingote vs moneda: ¿cuál elegir?**

| Criterio | Lingotes | Monedas bullion |
|---|---|---|
| Coste por gramo | Más bajo (>100g) | Más alto (prima 4-8%) |
| Liquidez | Buena en mercado profesional | Excelente, también en retail |
| Reconocimiento | Requiere certificado | Reconocimiento universal por diseño |
| Divisibilidad | Limitada | Excelente |
| Estética | Funcional | Atractivo añadido |
| Falsificación | Más vulnerable sin certificado | Diseño anti-falsificación complejo |

**La estrategia equilibrada** para un patrimonio entre 20.000€ y 200.000€:
- **60-70% en lingotes** de 100 g, 250 g y 1 kg (mejor coste)
- **20-30% en monedas bullion** de 1 oz para liquidez parcial
- **5-10% en fracciones pequeñas** (1/4 oz, 1/10 oz) para emergencias o regalos`,
      keyPoints: [
        'Las monedas bullion son oro de inversión con valor facial nominal y gran reconocimiento global.',
        'Krugerrand, Maple Leaf, Britannia y Filarmónica son las cuatro grandes.',
        'Las monedas tienen prima superior a los lingotes pero ofrecen mejor liquidez y divisibilidad.',
        'Estrategia óptima: combinar lingotes (mayoría del peso) con monedas (liquidez y flexibilidad).',
        'Bullion ≠ numismática: son mercados distintos.',
      ],
    },
    {
      slug: 'oro-numismatico',
      number: '2.4',
      title: 'Oro numismático: ¿inversión o coleccionismo?',
      durationMinutes: 16,
      videoUrl: null,
      content: `El oro numismático es probablemente la categoría más malentendida. Muchos compradores lo confunden con inversión en oro y acaban pagando primas del 50-200% sobre el valor metálico real, esperando que la "rareza" justifique el sobreprecio.

**Definición**. Una moneda numismática es una moneda valorada **principalmente por su valor histórico, su rareza, su estado de conservación y su demanda entre coleccionistas**, no por su contenido metálico. Una moneda romana de oro del emperador Augusto puede pesar 8 gramos pero valer 30.000€; su contenido metálico sería de unos 600€ a precio spot.

**Ejemplos típicos**: doblones españoles de la era colonial, sovereigns británicos antiguos (siglo XIX), monedas francesas (Napoleón), Águilas Doradas estadounidenses pre-1933, monedas papales, medievales.

**Las claves del valor numismático**:
1. **Rareza**: cuántas se acuñaron y cuántas sobreviven en estado coleccionable.
2. **Estado de conservación**: las monedas se gradúan (Sheldon: 1-70). Una en MS-65 puede valer 20 veces más que la misma en MS-50.
3. **Demanda del coleccionismo**: tendencias del mercado, modas.
4. **Procedencia y autenticación**: certificación por casas de grading reconocidas (PCGS, NGC, ANACS) puede multiplicar el valor.

**¿Es inversión o coleccionismo?**. Honestidad brutal: **el oro numismático es coleccionismo con potencial de inversión, no inversión pura**. Para que rinda como inversión, deben darse condiciones difíciles: compra por debajo de mercado, mantenimiento del estado, mercado activo al vender, conocimiento profundo del segmento.

Para alguien sin formación numismática, el riesgo es alto: pagar primas excesivas por monedas mediocres, comprar falsificaciones, o comprar a precio inflado a un coleccionista profesional.

**Ventajas reales (para quien sabe lo que hace)**:
- Potencial de revalorización superior al oro físico puro (las monedas top pueden multiplicar x10 en décadas).
- Disfrute estético y cultural.
- Discreción patrimonial.

**Desventajas para inversores patrimoniales**:
- Primas iniciales altísimas (50-200% sobre el valor metálico).
- Liquidez limitada: vender puede llevar meses.
- Volatilidad: el coleccionismo tiene ciclos.
- Riesgo de falsificación alto sin certificación profesional.
- Conocimiento técnico requerido durante años.

**Recomendación práctica**: si tu objetivo es **invertir en oro como reserva de valor**, ignora la numismática y céntrate en lingotes y monedas bullion modernas. La numismática solo es recomendable si tienes pasión genuina por la historia, capacidad de formación durante años, y aceptas que es más afición que inversión.

**Excepción interesante**: las **monedas bullion antiguas en estado bullion** (no premium grades). Por ejemplo, Krugerrands de los años 70-80 sin grading especial: en la práctica funcionan casi como bullion moderno con una pequeña prima histórica. Son una entrada razonable al universo numismático sin asumir todos sus riesgos.`,
      keyPoints: [
        'La numismática valora la moneda por rareza, estado e historia, no por contenido metálico.',
        'Las primas numismáticas suelen ser del 50-200% sobre el valor metálico.',
        'Para inversión patrimonial pura: enfócate en bullion y lingotes; deja la numismática como afición avanzada.',
        'La liquidez numismática es baja y depende de encontrar el coleccionista adecuado.',
        'Si te interesa empezar, considera bullion antiguas en estado básico antes que monedas con grading premium.',
      ],
    },
  ],
  quiz: [
    {
      id: 'm2-q1',
      question:
        '¿Cuál es la pureza mínima legal para que un lingote se considere "oro de inversión" a efectos fiscales en la UE?',
      options: ['90%', '95%', '99,5%', '99,99%'],
      correctIndex: 2,
      explanation:
        'La Directiva europea 98/80/CE establece que el oro de inversión debe tener pureza mínima del 99,5% para gozar de la exención de IVA.',
    },
    {
      id: 'm2-q2',
      question:
        '¿Qué moneda bullion tiene la pureza más alta entre las cuatro grandes (Maple Leaf, Krugerrand, Britannia, Filarmónica)?',
      options: [
        'Krugerrand',
        'Maple Leaf',
        'Britannia (versión clásica)',
        'Todas tienen la misma',
      ],
      correctIndex: 1,
      explanation:
        'La Maple Leaf canadiense tiene 99,99% de pureza desde su lanzamiento en 1979, frente al 91,67% del Krugerrand y la Britannia clásica.',
    },
    {
      id: 'm2-q3',
      question:
        'Si tienes 30.000€ para invertir en oro físico, ¿qué distribución de tamaños es más eficiente?',
      options: [
        'Todo en monedas de 1/10 oz para máxima divisibilidad',
        'Mezcla de lingotes de 100-250 g + algunas monedas de 1 oz',
        'Todo en lingotes de 1 g para repartir riesgo',
        'Todo numismática para máxima rentabilidad',
      ],
      correctIndex: 1,
      explanation:
        'La estrategia eficiente combina lingotes medianos (mejor prima por gramo) con monedas bullion (liquidez parcial). Las opciones extremas son ineficientes.',
    },
    {
      id: 'm2-q4',
      question: '¿Qué refinería NO está en la LBMA Good Delivery List?',
      options: [
        'Argor-Heraeus',
        'PAMP Suisse',
        'Refinería local sin certificación',
        'Valcambi',
      ],
      correctIndex: 2,
      explanation:
        'La LBMA Good Delivery List incluye refinerías auditadas con estándares de calidad mundial. Refinerías no listadas pueden ser legítimas pero no garantizan aceptación universal.',
    },
    {
      id: 'm2-q5',
      question: '¿Por qué la numismática NO es la mejor opción para un inversor patrimonial novato?',
      options: [
        'No es legal en España',
        'Su valor depende del precio del oro únicamente',
        'Tiene primas del 50-200%, baja liquidez y requiere conocimiento técnico profundo',
        'No se puede heredar',
      ],
      correctIndex: 2,
      explanation:
        'La numismática es coleccionismo con potencial de inversión, no inversión pura. Las primas altas, liquidez limitada y necesidad de expertise la hacen inadecuada para preservación patrimonial estándar.',
    },
  ],
};
